/**
 * Preview in Teams: one thunk, one API (getPreviewInTeamsUrl).
 * Handles IFS (upload + get URL) and OneDrive (get URL only). No legacy action dispatches.
 */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { PLEASE_WAIT } from "../../constants/messageTypes";
import UserAuth from "../../../auth/userAuth";
import { getPreviewInTeamsUrl } from "../../api/documentsAPI";
import { serializeError } from "../../utils/serializeError";
import { logger } from "../../../utils/logger";

const userAuth = new UserAuth();

/**
 * Single thunk for Preview in Teams.
 * Payload either:
 *   IFS:      { item, teamId, channelId, tenantId, navigate, returnTo?, returnState?, loadingMessage? }
 *   OneDrive: { documentId, fileRoot, teamId, channelId, tenantId, navigate, returnTo?, returnState?, loadingMessage? }
 */
const PREVIEW_DIALOG_AUTO_CLOSE_MS = 3000;

export const previewInTeams = createAsyncThunk(
  "documents/previewInTeams/previewInTeams",
  async (payload, { rejectWithValue, dispatch }) => {
    const { teamId, channelId, tenantId, navigate, returnTo, returnState } = payload;

    logger.info("[previewInTeams] thunk start", {
      hasItem: !!payload.item,
      ifsItem: payload.item
        ? {
            class: payload.item.class,
            docNo: payload.item.docNo,
            docRev: payload.item.docRev,
            docSheet: payload.item.sheetNo ?? payload.item.docSheet,
          }
        : null,
      documentId: payload.documentId,
      fileRoot: payload.fileRoot,
      returnTo,
      hasNavigate: typeof navigate === "function",
    }, "PREVIEW");

    const closeDialogAfterDelay = () => {
      setTimeout(() => {
        dispatch(closePreviewDialog());
      }, PREVIEW_DIALOG_AUTO_CLOSE_MS);
    };

    const tokenResponse = await userAuth.getUserToken(true);
    const accessToken = tokenResponse?.accessToken ?? null;
    if (!accessToken) {
      logger.warn("[previewInTeams] no access token", null, "PREVIEW");
      closeDialogAfterDelay();
      return rejectWithValue({ message: "No access token" });
    }

    const apiParams = {
      teamId,
      channelId,
      tenantId,
      item: payload.item,
      documentId: payload.documentId,
      fileRoot: payload.fileRoot,
    };

    try {
      const { data, status } = await getPreviewInTeamsUrl(accessToken, apiParams);

      logger.info("[previewInTeams] API response", {
        status,
        hasValue: data?.value != null,
        errorMessage: data?.errorMessage,
      }, "PREVIEW");

      if (status === 200 && data?.value != null) {
        if (typeof navigate === "function") {
          // IFS flow is: upload to OneDrive -> request preview URL.
          // Give OneDrive/Office services a moment to be ready to render,
          // otherwise the embedded viewer sometimes shows "no preview".
          if (payload?.item) {
            await new Promise((r) => setTimeout(r, 2000));
          }
          logger.info("[previewInTeams] navigating to preview", { returnTo }, "PREVIEW");
          navigate("/OpenDocIfsPreview", { state: { returnTo, returnState: returnState ?? {} } });
        }
        closeDialogAfterDelay();
        return data.value;
      }

      const errMsg = data?.errorMessage ?? (status === 404 ? "Preview not found" : `Preview URL status ${status}`);
      closeDialogAfterDelay();
      return rejectWithValue({ message: errMsg });
    } catch (err) {
      logger.error("[previewInTeams] catch", err, "PREVIEW");
      const pl = serializeError(err);
      // closeDialogAfterDelay();
      return rejectWithValue(pl ?? err);
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  status: "idle",
  loadingMessage: "",
  previewUrl: "",
  isOpen: false,
  message: "",
};

const previewInTeamsSlice = createSlice({
  name: "documents/previewInTeams",
  initialState,
  reducers: {
    closePreviewDialog: (state) => {
      state.isOpen = false; 
    },
    clearPreviewInTeamsError: (state) => {
      state.error = null;
    },
    clearPreviewUrl: (state) => {
      state.previewUrl = "";
    },
    setPreviewUrl: (state, action) => {
      state.previewUrl = action.payload ?? "";
    },
    setPreviewDialogOpen: (state, action) => {
      state.isOpen = action.payload ?? false;
    },
    /** Set error and message without running thunk (e.g. when hook returns early). */
    setPreviewError: (state, action) => {
      const payload = action.payload ?? {};
      state.loading = false;
      state.error = typeof payload === "object" && payload.message != null ? payload : { message: String(payload) };
      state.status = "error";
      state.loadingMessage = "";
      state.message = state.error?.message ?? "Preview failed.";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(previewInTeams.pending, (state, action) => {
        state.loading = true;
        state.error = null;
        state.status = "loading";
        state.loadingMessage = action.meta?.arg?.loadingMessage ?? PLEASE_WAIT;
        state.previewUrl = "";
        state.isOpen = true;
        state.message = "";
      })
      .addCase(previewInTeams.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.status = "success";
        state.loadingMessage = "";
        state.previewUrl = action.payload ?? "";
        state.message = "Preview opened.";
        state.isOpen = true;
      })
      .addCase(previewInTeams.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload ?? serializeError(action.error);
        state.status = "error";
        state.loadingMessage = "";
        state.previewUrl = "";
        state.isOpen = true;
        state.message = state.error?.message ?? "Preview failed.";
      });
  },
});

export const { clearPreviewInTeamsError,closePreviewDialog, clearPreviewUrl, setPreviewUrl, setPreviewDialogOpen, setPreviewError } = previewInTeamsSlice.actions;
export default previewInTeamsSlice.reducer;
